<script setup lang="ts">
import { useBillingUtils } from '@/composables/useBillingUtils';
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import { useBillingStore } from '@/stores/billing';
import { Alert, LayoutGap, Loader } from '@dabble/quillui';
import type { Stripe } from 'stripe';
import { computed, onMounted, ref, watch } from 'vue';

const props = defineProps<{ class?: string }>();

const billingStore = useBillingStore();
const { formatPrice, upcomingInvoicePaymentString } = useBillingUtils();
const { t } = useI18n();

const upcomingInvoiceData = ref<Stripe.Invoice | null>(null);
const loadingUpcoming = ref(false);

const customer = computed(() => billingStore.customer as Stripe.Customer | null);

const subscription = computed(() => billingStore.primarySubscription);

const className = computed(() => props.class || '');

const stripeBalance = computed(() => customer.value?.balance ?? 0);

const hasNonZeroBalance = computed(() => stripeBalance.value !== 0);

const balanceCurrency = computed(
  () => (customer.value?.currency || upcomingInvoiceData.value?.currency || 'usd') as string,
);

const balanceValueSuffix = computed(() => {
  const b = stripeBalance.value;
  if (b === 0) return '';
  if (b < 0) return t('billing_balance_credit_word');
  return t('billing_balance_owed_word');
});

const balanceAmountWithSuffix = computed(() => {
  const amt = formatBalance(stripeBalance.value, balanceCurrency.value);
  const suf = balanceValueSuffix.value;
  return suf ? `${amt} ${suf}` : amt;
});

const upcomingZeroChargeNote = computed(() => {
  const inv = upcomingInvoiceData.value;
  if (!inv || inv.amount_due !== 0) return '';
  return t('billing_upcoming_no_charge_detail');
});

const showBalanceRow = computed(() => hasNonZeroBalance.value);

const showNextPaymentRow = computed(() => {
  const sub = subscription.value;
  if (!sub) return false;
  if (sub.cancel_at_period_end) return false;
  return (
    sub.status === 'active' ||
    sub.status === 'trialing' ||
    sub.status === 'past_due' ||
    sub.status === 'paused' ||
    sub.status === 'unpaid'
  );
});

const showSection = computed(
  () => billingStore.hasStripeCustomer && (showBalanceRow.value || showNextPaymentRow.value),
);

async function loadNextInvoice() {
  if (!billingStore.hasStripeCustomer) {
    upcomingInvoiceData.value = null;
    return;
  }
  if (!showNextPaymentRow.value) {
    upcomingInvoiceData.value = null;
    return;
  }
  loadingUpcoming.value = true;
  try {
    upcomingInvoiceData.value = await billingStore.fetchUpcomingInvoice();
  } catch (err) {
    logError('Failed to load upcoming invoice:', err);
    upcomingInvoiceData.value = null;
  } finally {
    loadingUpcoming.value = false;
  }
}

function formatBalance(balance: number, currency: string): string {
  return formatPrice(Math.abs(balance), currency, true);
}

watch(
  () => [subscription.value?.id, subscription.value?.cancel_at_period_end, showNextPaymentRow.value],
  () => {
    loadNextInvoice();
  },
);

onMounted(() => {
  loadNextInvoice();
});
</script>

<template>
  <Alert v-if="showSection" variant="info" :class="['upcoming-billing-summary', className]" :no-icon="true">
    <div class="upcoming-billing-summary__inner">
      <div v-if="showBalanceRow" class="upcoming-billing-summary__row">
        <span class="upcoming-billing-summary__label">{{ t('billing_balance') }}{{ t('billing_label_suffix') }}</span>
        <span class="upcoming-billing-summary__value">{{ balanceAmountWithSuffix }}</span>
      </div>

      <div v-if="showNextPaymentRow" class="upcoming-billing-summary__row">
        <span class="upcoming-billing-summary__label"
          >{{ t('billing_next_payment') }}{{ t('billing_label_suffix') }}</span
        >
        <LayoutGap direction="column" size="none" class="upcoming-billing-summary__next">
          <span v-if="loadingUpcoming" class="upcoming-billing-summary__loading">
            <Loader />
          </span>
          <template v-else-if="upcomingInvoiceData">
            <span class="upcoming-billing-summary__next-main">{{
              upcomingInvoicePaymentString(upcomingInvoiceData as Stripe.Invoice)
            }}</span>
            <span v-if="upcomingZeroChargeNote" class="upcoming-billing-summary__next-sub">{{
              upcomingZeroChargeNote
            }}</span>
          </template>
          <span v-else>{{ t('billing_next_payment_none') }}</span>
        </LayoutGap>
      </div>
    </div>
  </Alert>
</template>

<style lang="scss" scoped>
.upcoming-billing-summary {
  margin-top: 10px;
  width: 100%;

  :deep(.alert__message) {
    width: 100%;
  }
}

.upcoming-billing-summary__inner {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  column-gap: 1rem;
  row-gap: 0.5rem;
  align-items: start;
  width: 100%;
  font-size: var(--font-size-sm, 0.875rem);
}

.upcoming-billing-summary__row {
  display: contents;
}

.upcoming-billing-summary__label {
  font-weight: 600;
  color: inherit;
}

.upcoming-billing-summary__value {
  text-align: left;
}

.upcoming-billing-summary__next {
  align-items: flex-start;
  text-align: left;
  min-width: 0;
}

.upcoming-billing-summary__loading {
  display: inline-flex;
  align-items: center;
  min-height: 1.2em;
}

.upcoming-billing-summary__next-main {
  display: block;
}

.upcoming-billing-summary__next-sub {
  display: block;
  font-size: 0.8125rem;
  line-height: 1.35;
  opacity: 0.95;
}
</style>
